import { CdkDragDrop } from "@angular/cdk/drag-drop";
import { CdkVirtualScrollViewport } from "@angular/cdk/scrolling";
import { Component, computed, DestroyRef, effect, HostBinding, inject, input, Optional, Signal, signal, viewChild } from "@angular/core";
import { DragAndDropService } from "@app/drag-and-drop/drag-and-drop.service";
import { CardViewService, HelpersService, LocalStorageService, ResponseErrorHandlerService } from "@app/shared";
import { isUnassigned } from "@shared/users-service";
import { CardDragAndDropService } from "@app/shared/card-drag-and-drop-service";
import { IBaseCard, ITaxReturnStatus } from "@app/views-interfaces";
import { IMergedStatusObjectModel } from "@app/views-interfaces/status-object-model.interface";
import { isJobsBilling } from "@app/views-interfaces/ApplicationType.enum";
import { CountersService } from "../shared/counters-service/counters.service";
import { DashboardsService } from "../shared/dashboards-service/dashboards.service";
import { PrinterCardsService } from "../shared/printer-cards-service/printer-cards.service";
import { IColumnData } from "../views-interfaces/column-data.interface";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ColumnStateService } from "../assignees-columns-wrapper/column-state.service";

@Component({
    selector: "column-component",
    templateUrl: "./column.component.html",
    styleUrl: "./column.component.scss",
    standalone: false,
    host: { class: `wk-column` },
})
export class ColumnComponent {
    private readonly destroyRef = inject(DestroyRef);
    protected readonly cardDragAndDropService = inject(CardDragAndDropService);
    private readonly cardViewService = inject(CardViewService);
    protected readonly countersService = inject(CountersService);
    private readonly dashboardsService = inject(DashboardsService);
    private readonly dragAndDropService = inject(DragAndDropService);
    private readonly helpersService = inject(HelpersService);
    // Optional: only present when rendered inside AssigneesColumnsWrapperComponent
    private readonly columnStateService = inject(ColumnStateService, { optional: true });
    private readonly printerCardsService = inject(PrinterCardsService);
    private readonly responseErrorHandlerService = inject(ResponseErrorHandlerService);

    readonly columnData = input.required<IColumnData<IBaseCard>>();
    readonly selectedStatus = input<IMergedStatusObjectModel | undefined>(undefined);
    readonly selectedStepId = input<string | undefined>(undefined);
    readonly isHidden = input(false);
    readonly printing = input(false);
    readonly scrollbarGutterStable = input(false);
    readonly dragDisabled = input(false);

    protected readonly cards = computed<IBaseCard[]>(() => this.columnData().cards ?? []);
    protected readonly containerHeight = computed<number>(() => (this.cards().length ? this.cards().length * 111 : 0));
    protected readonly clientFileCount = computed<number>(() =>
        this.cards().reduce((sum, card) => sum + this.helpersService.getClientFileCount(card), 0),
    );
    protected displayMode = "";
    protected expanded = signal(true);
    protected hovering = signal(false);
    private columnsStorageKey = "";

    protected readonly dragging: Signal<boolean> = this.dragAndDropService.isDragging;
    protected readonly dragOriginalCardId: Signal<string> = this.cardDragAndDropService.dragOriginalCardId;
    protected readonly dragOriginalUserId: Signal<string | null> = this.cardDragAndDropService.dragOriginalUserId;

    protected readonly isUnassigned = isUnassigned;

    protected readonly isJobsBillingDashboard = computed(() => {
        const currentDashboard = this.dashboardsService.currentDashboard();
        return isJobsBilling(currentDashboard?.ApplicationType);
    });

    protected readonly disabledDragCardIds = computed<Set<string>>(() => this.cardDragAndDropService.getDisabledDragCardIds(this.cards()));

    protected readonly scrollActiveColumns = viewChild(CdkVirtualScrollViewport);
    protected readonly hasScrollbar = signal<boolean>(false);
    protected readonly statuses = computed<ITaxReturnStatus[]>(
        () =>
            this.columnData()
                .statuses?.filter((status) => status.TaxReturnStatus)
                .map((status) => status.TaxReturnStatus) ?? [],
    );

    protected readonly statusIds = computed<string[]>(() =>
        this.statuses()
            .map((status) => status.Id)
            .filter((id): id is string => !!id),
    );

    constructor() {
        this.cardViewService.viewDisplayModeChangeEvent$.pipe(takeUntilDestroyed()).subscribe(() => {
            this.displayMode = this.cardViewService.currentViewDisplayMode;
        });

        effect(() => {
            this.cards(); // track card changes to re-check viewport size
            setTimeout(() => this.scrollActiveColumns()?.checkViewportSize());
        });

        effect(() => {
            const scrollActiveColumns = this.scrollActiveColumns();
            if (!scrollActiveColumns) return;

            const el = scrollActiveColumns.elementRef.nativeElement as HTMLElement;
            this.hasScrollbar.set(el.scrollHeight > el.clientHeight);

            const ro = new ResizeObserver((entries) => {
                if (this.dragAndDropService.isDragging()) return;
                for (const e of entries) {
                    this.hasScrollbar.set(el.scrollHeight > el.clientHeight);
                }
            });
            ro.observe(el);

            this.destroyRef.onDestroy(() => ro.disconnect());
            return () => ro.disconnect();
        });

        effect(() => {
            const printing = this.printing();
            if (!printing) return;
            const columnData = this.columnData();
            if (!this.isHidden() && this.cardViewService.currentViewType === columnData.state && columnData.cards) {
                this.printerCardsService.printData(
                    columnData.cards.reduce((acc, it) => {
                        acc[it.cardId] = it;
                        return acc;
                    }, {}),
                    columnData.state,
                    columnData.statuses.map((status) => status.TaxReturnStatus.Id).filter((id): id is string => !!id),
                );
            }
        });

        effect(() => {
            let dashboard = this.dashboardsService.currentDashboard();
            if (dashboard) {
                const key = this.helpersService.getCurrentUser().sub + dashboard.Id + dashboard.TaxYear + this.cardViewService.currentViewType;
                const recentView = localStorage.getItem(key);
                this.displayMode = recentView ?? "Kanban";
                this.columnsStorageKey = this.helpersService.getCurrentUser().sub + dashboard.Id + dashboard.TaxYear;
                const viewTypeStorageKey = this.cardViewService.currentViewType;
                const displayModeStorageKey = this.cardViewService.currentViewDisplayMode;
                let stepIdStorageKey = undefined;
                let statusIdStorageKey = undefined;
                if (displayModeStorageKey === "details") {
                    stepIdStorageKey = this.selectedStepId();
                    statusIdStorageKey = this.selectedStatus()?.TaxReturnStatus.Id;
                }
                const columnIdStorageKey = this.columnData().id === "" ? this.columnData().assignedUserId : this.columnData().id;
                this.expanded.set(
                    this.getLocalStorage(viewTypeStorageKey, displayModeStorageKey, stepIdStorageKey, statusIdStorageKey, columnIdStorageKey),
                );
            }
        });

        // Keep ColumnStateService in sync so the horizontal scroll strategy knows each column's width.
        // Uses effect cleanup so deregistration happens automatically when the column is destroyed
        // or when columnData changes (virtual scroll recycling).
        if (this.columnStateService) {
            effect((onCleanup) => {
                const colId = this.columnData().id || this.columnData().assignedUserId;
                const expanded = this.expanded();
                this.columnStateService!.setExpanded(colId, expanded);
                onCleanup(() => this.columnStateService!.deregister(colId));
            });
        }
    }

    @HostBinding("class.wk-ckd")
    get isKanbanOrDetails() {
        return this.displayMode === "Kanban" || this.displayMode === "details";
    }

    @HostBinding("class.wk-cl")
    get isList() {
        return this.displayMode === "List";
    }

    onColumnHover() {
        if (this.dragging()) {
            this.hovering.set(true);
        }
    }

    onColumnHoverEnd() {
        this.hovering.set(false);
    }

    toggleColumnWidth() {
        this.expanded.update((expanded) => !expanded);
        const viewTypeStorageKey = this.cardViewService.currentViewType;
        const displayModeStorageKey = this.cardViewService.currentViewDisplayMode;
        let stepIdStorageKey = undefined;
        let statusIdStorageKey = undefined;
        if (displayModeStorageKey === "details") {
            stepIdStorageKey = this.selectedStepId();
            statusIdStorageKey = this.selectedStatus()?.TaxReturnStatus.Id;
        }
        const columnIdStorageKey = this.columnData().id === "" ? this.columnData().assignedUserId : this.columnData().id;
        this.setLocalStorage(viewTypeStorageKey, displayModeStorageKey, stepIdStorageKey, statusIdStorageKey, columnIdStorageKey, this.expanded());
    }

    getLocalStorage(viewType: string, displayMode: string, stepId: string, statusId: string, columnId: string): boolean {
        const item = localStorage.getItem(this.columnsStorageKey);
        const columnsLocalStorage = item && item !== "" ? JSON.parse(item) : {};
        let result = true;
        if (columnsLocalStorage[viewType]) {
            let valueLocalStorage = columnsLocalStorage[viewType];
            if (columnsLocalStorage[viewType][displayMode]) {
                valueLocalStorage = valueLocalStorage[displayMode];
                if (displayMode === "details") {
                    if (valueLocalStorage[stepId]) {
                        valueLocalStorage = valueLocalStorage[stepId];
                        if (valueLocalStorage[statusId]) {
                            valueLocalStorage = valueLocalStorage[statusId];
                            if (valueLocalStorage[columnId] !== undefined) {
                                result = valueLocalStorage[columnId] === 1;
                            }
                        }
                    }
                } else {
                    if (valueLocalStorage[columnId] !== undefined) {
                        result = columnsLocalStorage[columnId] === 1;
                    }
                }
            }
        }
        return result;
    }

    setLocalStorage(viewType: string, displayMode: string, stepId: string, statusId: string, columnId: string, value: boolean) {
        if (displayMode === "details") {
            if (value) {
                LocalStorageService.removeObject(this.columnsStorageKey, [viewType, displayMode, stepId, statusId, columnId]);
            } else {
                LocalStorageService.setObject(this.columnsStorageKey, [viewType, displayMode, stepId, statusId, columnId], 0);
            }
        } else {
            if (value) {
                LocalStorageService.removeObject(this.columnsStorageKey, [viewType, displayMode, columnId]);
            } else {
                LocalStorageService.setObject(this.columnsStorageKey, [viewType, displayMode, columnId], 0);
            }
        }
    }

    protected onDragAttemptedWhileDisabled(): void {
        if (!this.dragDisabled()) return;
        this.responseErrorHandlerService.showPopup({ typeLayout: "ALERT", messageCode: "dragDisabledTooManyColumns" });
    }

    protected onDrop($event: CdkDragDrop<any>, statusId: string, targetUserId: string) {
        const card: IBaseCard = $event.item.data;

        if (!$event.isPointerOverContainer) return;

        this.cardDragAndDropService.onDrop(card, statusId, targetUserId);
    }

    protected readonly disabledDropListIds = computed<Set<string>>(() => {
        const columnData = this.columnData();
        const statusIds = this.statusIds();
        if (!statusIds) return new Set<string>();

        return this.cardDragAndDropService.getDisabledDropListIds(statusIds, columnData.assignedUserId ?? "", columnData.active ?? true);
    });

    protected trackByCardId(_index: number, card: IBaseCard): string {
        return card.cardId;
    }

    protected readonly unavailableStatusIds = computed<Set<string>>(() => {
        if (!this.isJobsBillingDashboard()) return new Set<string>();
        const originalStatusIds = this.cardDragAndDropService.dragOriginalStatusIds();
        return new Set([...this.disabledDropListIds()].filter((id) => !originalStatusIds.has(id)));
    });
}
